import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Textarea,
} from '@napaicp/ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  useAcceptMyJudgeInvitation,
  useDeclineMyJudgeInvitation,
  useMyJudgeInvitations,
} from '@/features/judge-invitations/api'

/**
 * Judge invitations addressed to the signed-in person's verified email. The
 * existing-account invitation email only carries a sign-in link (no token), so
 * accepting or declining happens here. Renders nothing when there is nothing
 * to answer or the lookup fails: a missing card never blocks the overview.
 */
export function PendingJudgeInvitations() {
  const { t } = useTranslation('home')
  const invitations = useMyJudgeInvitations()
  const accept = useAcceptMyJudgeInvitation()
  const decline = useDeclineMyJudgeInvitation()
  const [decliningId, setDecliningId] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [accepted, setAccepted] = useState<Array<{ id: string; competitionId: string }>>([])
  const [declinedIds, setDeclinedIds] = useState<string[]>([])

  const pending = invitations.data ?? []
  if (pending.length === 0 && accepted.length === 0 && declinedIds.length === 0) return null

  return (
    <Card role="region" aria-labelledby="judge-invitations-heading">
      <CardHeader>
        <CardTitle id="judge-invitations-heading" className="text-xl">
          {t('judgeInvitations.title')}
        </CardTitle>
        <CardDescription className="text-base">{t('judgeInvitations.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {pending.map((invitation) => (
          <div
            key={invitation.id}
            className="flex flex-col gap-2 rounded-md border border-border p-3"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{invitation.competitionName}</span>
              <span className="text-xs text-muted-foreground">
                {t('judgeInvitations.expires', {
                  date: new Date(invitation.expiresAt).toLocaleDateString(),
                })}
              </span>
            </div>
            {decliningId === invitation.id ? (
              <div className="flex flex-col gap-2">
                <label className="flex flex-col gap-1 text-sm">
                  {t('judgeInvitations.reasonLabel')}
                  <Textarea
                    value={reason}
                    maxLength={1000}
                    onChange={(event) => {
                      setReason(event.target.value)
                    }}
                  />
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={decline.isPending}
                    onClick={() => {
                      decline.mutate(
                        { invitationId: invitation.id, reason },
                        {
                          onSuccess: () => {
                            setDeclinedIds((ids) => [...ids, invitation.id])
                            setDecliningId(null)
                            setReason('')
                          },
                        },
                      )
                    }}
                  >
                    {t('judgeInvitations.confirmDecline')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDecliningId(null)
                      setReason('')
                    }}
                  >
                    {t('judgeInvitations.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={accept.isPending}
                  onClick={() => {
                    accept.mutate(invitation.id, {
                      onSuccess: (result) => {
                        setAccepted((list) => [
                          ...list,
                          { id: invitation.id, competitionId: result.competitionId },
                        ])
                      },
                    })
                  }}
                >
                  {t('judgeInvitations.accept')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDecliningId(invitation.id)
                    setReason('')
                  }}
                >
                  {t('judgeInvitations.decline')}
                </Button>
              </div>
            )}
          </div>
        ))}
        {accepted.map((entry) => (
          <p key={entry.id} role="status" className="text-sm">
            {t('judgeInvitations.accepted')}{' '}
            <Link
              className="underline underline-offset-4"
              to={`/judge/assignments?competitionId=${encodeURIComponent(entry.competitionId)}`}
            >
              {t('judgeInvitations.goToJudging')}
            </Link>
          </p>
        ))}
        {declinedIds.length > 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            {t('judgeInvitations.declined')}
          </p>
        ) : null}
        {accept.isError || decline.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {t('judgeInvitations.error')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
